import { useQuery } from '@tanstack/react-query'
import { CHAIN_CONFIG, CHAIN_INFO_LIST, STRIDE_CHAIN_INFO, SUPPORTED_COIN_DENOMS } from '@/config'
import { SelectedCoinDenom } from '@/wallet-utils'
import { redis } from '@/redis'
import { queryKeys } from '@/query-keys'
import axios from 'axios'
import { z } from 'zod'

export interface ApyUnbondingPeriod {
  start: number
  end: number
  unit: string
}

export interface Apy {
  currentYield: number
  strideYield: number
  unbondingPeriod: ApyUnbondingPeriod
  fees: number
}

type DenomApyMap = Record<SelectedCoinDenom, Apy>

const UNBONDING_BUFFER_DAYS = 3

const getHostZoneUnbondingDays = async (denom: SelectedCoinDenom): Promise<number> => {
  const chainInfo = CHAIN_INFO_LIST[denom]
  const instance = axios.create({ baseURL: STRIDE_CHAIN_INFO.rest })

  if (denom === 'DYM') {
    const response = await instance.get(`Stride-Labs/stride/stakedym/host_zone`)
    const seconds = Number(response.data.host_zone.unbonding_period_seconds)
    return Math.ceil(seconds / 86400)
  }

  const response = await instance.get(`Stride-Labs/stride/stakeibc/host_zone/${chainInfo.chainId}`)
  return Number(response.data.host_zone.unbonding_period)
}

const useStakeStatsQuery = () => {
  const getChainStats = async (denom: SelectedCoinDenom): Promise<Apy> => {
    const [{ apy, apr }, unbondingDays, fees] = await Promise.all([
      getChainApy(denom),
      getHostZoneUnbondingDays(denom),
      redis.get<number>(`${CHAIN_INFO_LIST[denom].chainId}-fees`)
    ])

    return {
      currentYield: apr ?? 0,
      strideYield: apy ?? 0,
      unbondingPeriod: { start: unbondingDays, end: unbondingDays + UNBONDING_BUFFER_DAYS, unit: 'days' },
      fees: fees || 0
    }
  }

  const handleFetchApy = async (): Promise<DenomApyMap> => {
    // @TODO: Refactor to probably use Object.entries/Object.fromEntries instead
    const results = await Promise.all(
      SUPPORTED_COIN_DENOMS.map(async (denom) => {
        return [denom, await getChainStats(denom)]
      })
    )

    return Object.fromEntries(results)
  }

  return useQuery({
    queryKey: queryKeys.globalApy,
    queryFn: handleFetchApy
  })
}

const ChainApyResponseSchema = z.object({
  denom: z.string(),
  apy: z.number().nullable(),
  apr: z.number().nullable()
})

const getChainApy = async (denom: SelectedCoinDenom) => {
  const response = await axios.get(`https://edge.stride.zone/api/${CHAIN_CONFIG[denom].edgeServerId}/stats/apy`)
  return ChainApyResponseSchema.parse(response.data)
}

export { useStakeStatsQuery, getChainApy }
